import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background grain px-4 py-10" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/"
          className="brutal-btn inline-flex items-center gap-2 px-3 py-2 bg-card mb-6"
        >
          <ArrowLeft size={14} strokeWidth={2.5} /> Back
        </Link>

        <article className="brutal-card bg-card p-6 md:p-10 space-y-5">
          <header>
            <h1 className="text-4xl font-display">HAEVEN Privacy Policy</h1>
            <p className="text-muted-foreground mt-2">Effective: August 13, 2026</p>
          </header>

          <p>
            HAEVEN is operated by RMC FAMILY ENTERPRISES LLC ("HAEVEN," "we," "us," or "our").
            This Privacy Policy explains what information we collect, how we use and share it, and the
            choices available to you when you use the HAEVEN website, mobile application, and related services.
          </p>

          <section>
            <h2 className="text-2xl font-display mt-6">1. Information We Collect</h2>
            <p>Depending on how you use HAEVEN, we may collect the following categories of information:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li><strong>Account information:</strong> email address, username, display name, password credentials or authentication identifiers, and account settings.</li>
              <li><strong>Profile information:</strong> profile photo, biography, privacy preferences, and other information you choose to add to your profile.</li>
              <li><strong>User content:</strong> posts, comments, likes, stories, community activity, direct messages, uploaded images or media, journal entries, and other content you create or submit.</li>
              <li><strong>Social and interaction data:</strong> follows, followers, communities, conversations, reactions, and other interactions with people or content on HAEVEN.</li>
              <li><strong>Device and technical information:</strong> device type, operating system, app version, IP address, diagnostic information, crash information, and similar technical data needed to operate and secure the service.</li>
              <li><strong>Notification information:</strong> device or push-notification tokens and your notification preferences when you enable notifications.</li>
              <li><strong>Purchase and subscription information:</strong> product or subscription status and transaction-related identifiers provided by the applicable app store or payment platform. We do not receive your full payment-card number from Apple or Google.</li>
              <li><strong>Advertising and partner interactions:</strong> impressions and clicks on clearly labeled sponsor, promoted, or affiliate offers, when these features are active.</li>
              <li><strong>First-party purchase-funnel activity:</strong> HAEVEN Premium paywall views, plan selections, checkout starts, activations, restores, and billing-management actions used to improve the purchase experience. These events do not include journal text, messages, payment-card data, or cross-app tracking identifiers.</li>
              <li><strong>Safety and moderation information:</strong> reports, blocks, moderation decisions, and signals used to detect spam, abuse, threats, fraud, or violations of our rules.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-display mt-6">2. How We Use Information</h2>
            <p>We may use information to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Create, authenticate, and maintain your account.</li>
              <li>Provide feeds, profiles, communities, messaging, wellness, journal, premium, and other HAEVEN features.</li>
              <li>Deliver, personalize, maintain, and improve the service.</li>
              <li>Process subscription status and provide paid features.</li>
              <li>Measure the first-party HAEVEN Premium purchase funnel, diagnose checkout problems, and improve conversion without cross-app behavioral advertising.</li>
              <li>Show and measure clearly labeled contextual sponsor, promoted, or affiliate offers to non-premium users.</li>
              <li>Send service-related notifications and communications you request.</li>
              <li>Protect users and the service from spam, fraud, abuse, harassment, threats, and other harmful activity.</li>
              <li>Enforce our Terms, community rules, and legal obligations.</li>
              <li>Diagnose technical problems, monitor reliability, and improve security.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-display mt-6">3. Automated Safety and Moderation</h2>
            <p>
              HAEVEN may use automated systems and service providers to help review or analyze content and
              activity for safety, moderation, spam prevention, abuse detection, and policy enforcement.
              This processing may include content you submit, including posts and messages, when necessary
              to provide safety and moderation functions. We use these systems to support a safer social experience,
              not to sell the content of your private communications.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-display mt-6">4. How We Share Information</h2>
            <p>We may share information in the following circumstances:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li><strong>With other users:</strong> information and content you choose to make visible through your profile, posts, communities, and social interactions, subject to your settings and the design of each feature.</li>
              <li><strong>With service providers:</strong> vendors that help us provide hosting, cloud infrastructure, authentication, analytics, diagnostics, notifications, safety/moderation, customer support, and other operational services.</li>
              <li><strong>With app stores and payment platforms:</strong> when needed to validate purchases, subscriptions, refunds, or entitlement status.</li>
              <li><strong>With sponsors and affiliate partners:</strong> when you choose to open a clearly labeled partner offer. The partner’s own privacy policy applies after you leave HAEVEN.</li>
              <li><strong>For legal and safety reasons:</strong> when we reasonably believe disclosure is necessary to comply with law, protect rights or safety, investigate fraud or abuse, or enforce our agreements.</li>
              <li><strong>Business transfers:</strong> if HAEVEN or relevant assets are involved in a merger, acquisition, financing, reorganization, or sale, subject to applicable law.</li>
            </ul>
            <p className="mt-2">We do not sell your personal information for money. HAEVEN may earn revenue from sponsor impressions, clicks, or qualifying affiliate purchases.</p>
          </section>

          <section>
            <h2 className="text-2xl font-display mt-6">5. Data Retention</h2>
            <p>
              We retain personal information for as long as reasonably necessary to provide HAEVEN, maintain
              security, comply with legal obligations, resolve disputes, and enforce our agreements. Retention
              periods can vary depending on the type of information and why it is needed. Some information may
              remain in backups or be retained where required for security, fraud prevention, or legal compliance.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-display mt-6">7. Your Choices and Rights</h2>
            <p>
              You can update certain account and profile information from within HAEVEN. You may also have rights,
              depending on your location, to request access to, correction of, deletion of, or a copy of certain
              personal information. You may change notification permissions through your device settings.
            </p>
            <p className="mt-2">
              To request account or personal-data deletion, use any deletion controls available in HAEVEN or contact
              us at <a className="underline" href="mailto:support@rmcfamilyenterprises.com">support@rmcfamilyenterprises.com</a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-display mt-6">8. Security</h2>
            <p>
              We use reasonable administrative, technical, and organizational safeguards designed to protect personal
              information. No method of transmission or storage is completely secure, so we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-display mt-6">9. Children</h2>
            <p>
              HAEVEN is not intended for children under 13, and we do not knowingly collect personal information from
              children under 13. If we learn that we have collected such information, we will take reasonable steps to delete it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-display mt-6">10. International Processing</h2>
            <p>
              Your information may be processed in the United States or other countries where our service providers operate.
              Where required, we use appropriate measures for international data transfers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-display mt-6">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. When we make material changes, we will update the effective
              date and provide additional notice when required by law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-display mt-6">12. Contact Us</h2>
            <p>
              For privacy questions, requests, or concerns, contact:<br />
              <strong>RMC FAMILY ENTERPRISES LLC</strong><br />
              Email: <a className="underline" href="mailto:support@rmcfamilyenterprises.com">support@rmcfamilyenterprises.com</a>
            </p>
          </section>
        </article>
      </div>
    </div>
  );
}
